import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import axios from "axios";
import API_ENDPOINTS from "config/api";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

export default function LibroMayor() {
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState("");
  const [fechaInicio, setFechaInicio] = useState("2024-11-01");
  const [fechaFin, setFechaFin] = useState("2024-11-30");
  const [movimientos, setMovimientos] = useState([]);
  const [cuentasDisponibles, setCuentasDisponibles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cargar lista de cuentas al inicio
  useEffect(() => {
    const fetchCuentas = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.CONTABILIDAD.CUENTAS);
        setCuentasDisponibles(response.data);
      } catch (error) {
        console.error("Error al cargar cuentas:", error);
      }
    };
    fetchCuentas();
  }, []);

  // Cargar movimientos cuando cambian los filtros
  useEffect(() => {
    const fetchMovimientos = async () => {
      if (!cuentaSeleccionada) {
        setMovimientos([]);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(API_ENDPOINTS.CONTABILIDAD.LIBRO_MAYOR, {
          params: {
            cuenta: cuentaSeleccionada,
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
          },
        });
        setMovimientos(response.data.movimientos || []);
      } catch (error) {
        console.error("Error al cargar movimientos del libro mayor:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMovimientos();
  }, [cuentaSeleccionada, fechaInicio, fechaFin]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  const calcularSaldos = () => {
    const cuenta = cuentasDisponibles.find((c) => c.codigo === cuentaSeleccionada);
    let saldo = cuenta ? cuenta.saldoInicial : 0;
    let totalDebe = 0;
    let totalHaber = 0;

    const rows = movimientos.map((mov) => {
      totalDebe += mov.debe;
      totalHaber += mov.haber;
      saldo = saldo + mov.debe - mov.haber;

      return {
        fecha: (
          <MDTypography variant="caption" fontWeight="medium">
            {new Date(mov.fecha).toLocaleDateString("es-MX")}
          </MDTypography>
        ),
        asiento: (
          <MDTypography variant="caption" fontWeight="medium">
            {mov.asiento}
          </MDTypography>
        ),
        concepto: <MDTypography variant="caption">{mov.concepto}</MDTypography>,
        debe: (
          <MDTypography
            variant="caption"
            fontWeight="medium"
            color={mov.debe > 0 ? "success" : "text"}
          >
            {mov.debe > 0 ? formatCurrency(mov.debe) : "-"}
          </MDTypography>
        ),
        haber: (
          <MDTypography
            variant="caption"
            fontWeight="medium"
            color={mov.haber > 0 ? "error" : "text"}
          >
            {mov.haber > 0 ? formatCurrency(mov.haber) : "-"}
          </MDTypography>
        ),
        saldo: (
          <MDTypography variant="caption" fontWeight="bold">
            {formatCurrency(saldo)}
          </MDTypography>
        ),
      };
    });

    return { rows, totalDebe, totalHaber, saldoFinal: saldo };
  };

  const { rows, totalDebe, totalHaber, saldoFinal } = cuentaSeleccionada
    ? calcularSaldos()
    : { rows: [], totalDebe: 0, totalHaber: 0, saldoFinal: 0 };

  const columns = [
    { Header: "fecha", accessor: "fecha", width: "12%", align: "left" },
    { Header: "asiento", accessor: "asiento", width: "10%", align: "left" },
    { Header: "concepto", accessor: "concepto", width: "40%", align: "left" },
    { Header: "debe", accessor: "debe", align: "right" },
    { Header: "haber", accessor: "haber", align: "right" },
    { Header: "saldo", accessor: "saldo", align: "right" },
  ];

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Libro Mayor
              </MDTypography>

              {/* Filtros */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Seleccionar Cuenta"
                    value={cuentaSeleccionada}
                    onChange={(e) => setCuentaSeleccionada(e.target.value)}
                  >
                    <MenuItem value="">Ninguna</MenuItem>
                    {cuentasDisponibles.map((cuenta) => (
                      <MenuItem key={cuenta.id} value={cuenta.codigo}>
                        {cuenta.codigo} - {cuenta.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha Inicio"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha Fin"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDButton variant="gradient" color="info" fullWidth sx={{ height: "100%" }}>
                    <Icon>print</Icon>&nbsp;Imprimir
                  </MDButton>
                </Grid>
              </Grid>

              {/* Resumen de cuenta */}
              {cuentaSeleccionada && (
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} md={3}>
                    <Card variant="outlined">
                      <MDBox p={2} textAlign="center">
                        <MDTypography variant="caption" color="text">
                          Saldo Inicial
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="bold">
                          {formatCurrency(
                            cuentasDisponibles.find((c) => c.codigo === cuentaSeleccionada)
                              ?.saldoInicial || 0
                          )}
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card variant="outlined">
                      <MDBox p={2} textAlign="center">
                        <MDTypography variant="caption" color="text">
                          Total Cargos
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="bold" color="success">
                          {formatCurrency(totalDebe)}
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card variant="outlined">
                      <MDBox p={2} textAlign="center">
                        <MDTypography variant="caption" color="text">
                          Total Abonos
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="bold" color="error">
                          {formatCurrency(totalHaber)}
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card variant="outlined">
                      <MDBox p={2} textAlign="center">
                        <MDTypography variant="caption" color="text">
                          Saldo Final
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="bold" color="info">
                          {formatCurrency(saldoFinal)}
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Tabla de movimientos */}
              {cuentaSeleccionada ? (
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              ) : (
                <MDBox textAlign="center" py={5}>
                  <Icon fontSize="large" color="action">
                    menu_book
                  </Icon>
                  <MDTypography variant="h6" color="text" mt={2}>
                    Seleccione una cuenta para ver su libro mayor
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
